import { useEffect, useState } from "react";
import { Loader2, CheckCircle } from "lucide-react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { LOADING_DURATIONS } from "@/config/loadingDurations";

interface LoadingStep {
  id: number;
  text: string;
  delay: number;
}

const loadingSteps: LoadingStep[] = [
  {
    id: 1,
    text: "Verificare plată cu Stripe...",
    delay: LOADING_DURATIONS.paymentProcessing[0],
  },
  {
    id: 2,
    text: "Adăugare credite în contul tău...",
    delay: LOADING_DURATIONS.paymentProcessing[1],
  },
  {
    id: 3,
    text: "Finalizare tranzacție...",
    delay: LOADING_DURATIONS.paymentProcessing[2],
  },
];

export function LoadingPaymentProcessing() {
  const [completedSteps, setCompletedSteps] = useState<number[]>([]);

  useEffect(() => {
    loadingSteps.forEach((step) => {
      setTimeout(() => {
        setCompletedSteps((prev) => [...prev, step.id]);
      }, step.delay);
    });
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md bg-muted/30 border-0 shadow-lg">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="p-4 rounded-full bg-primary/5">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          </div>
          <div className="space-y-2">
            <h2 className="text-2xl font-semibold tracking-tight">
              Procesare plată în curs...
            </h2>
            <p className="text-sm text-muted-foreground">
              Confirmăm plata ta cu Stripe. Acest proces durează de obicei
              câteva secunde.
            </p>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {loadingSteps.map((step) => {
              const isCompleted = completedSteps.includes(step.id);
              const isActive =
                !isCompleted &&
                (step.id === 1 || completedSteps.includes(step.id - 1));

              return (
                <div
                  key={step.id}
                  className={`flex items-center gap-3 p-3 rounded-lg transition-all duration-300 ${
                    isCompleted
                      ? "bg-primary/5"
                      : isActive
                        ? "bg-primary/5"
                        : "opacity-50"
                  }`}
                >
                  <div className="flex-shrink-0">
                    {isCompleted ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : isActive ? (
                      <Loader2 className="h-5 w-5 animate-spin text-primary" />
                    ) : (
                      <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30" />
                    )}
                  </div>
                  <p
                    className={`text-sm font-medium ${
                      isCompleted
                        ? "text-green-600"
                        : isActive
                          ? "text-primary"
                          : "text-muted-foreground"
                    }`}
                  >
                    {step.text}
                  </p>
                </div>
              );
            })}
          </div>
          <div className="mt-6 text-center">
            <p className="text-xs text-muted-foreground">
              Te rugăm să nu închizi această pagină până la finalizarea
              procesului.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
